import Decimal from "decimal.js-light";
import { Suspense, useCallback, useMemo, useState } from "react";

import { useAuth } from "~/contexts/AuthContext";
import { useLazyDialog } from "~/hooks/useLazyDialog.ts";
import type { Absence } from "~/modules/absence/api/absence/absenceTypes.ts";
import type { AbsenceType } from "~/modules/absence/api/absenceType/absenceTypeTypes.ts";
import { useAllHolidays } from "~/modules/absence/api/holiday/holidayQueries.ts";
import {
	useStaffMembersVacationLedgerReport,
} from "~/modules/absence/api/vacationLedgerEntry/vacationLedgerEntryQueries.ts";
import { useStaffMembersWorkingSchedules } from "~/modules/absence/api/workingSchedule/workingScheduleQueries.ts";
import { CreateAbsenceSidebar, DeleteAbsenceModal, UpdateAbsenceSidebar } from "~/modules/absence/components/AbsencesView/lazyAbsenceDialogs.ts";
import { PermissionNames } from "~/types/entityNames.ts";

type CreatePreset = {
	absenceTypeId?: string;
	date?: Date;
};

type UseAbsenceActionsParams = {
	absences: Absence[];
	absenceTypes: AbsenceType[];
	staffMemberId: string;
};

/**
 * Loads the inputs that the absence sidebars need for one staff member. Returns the openers and the
 * `dialogs` element: render it once. The dialogs show only after the inputs are loaded.
 * `createPanelInputs` hands the same inputs to a composer of `CreateAbsencePanel`. It is `null` until they are loaded.
 */
export const useAbsenceActions = ({ absences, absenceTypes, staffMemberId }: UseAbsenceActionsParams) => {
	const { hasAnyPermission } = useAuth();
	const userCanManageAbsences = hasAnyPermission(PermissionNames.CanManageAbsences);

	const { data: vacationLedgerReport } = useStaffMembersVacationLedgerReport(staffMemberId);
	const { data: workingSchedules } = useStaffMembersWorkingSchedules(staffMemberId);
	const { data: holidays } = useAllHolidays();

	const [createIsOpen, setCreateIsOpen] = useState(false);
	const [createPreset, setCreatePreset] = useState<CreatePreset>({});
	const [absenceIdToUpdate, setAbsenceIdToUpdate] = useState<string | null>(null);
	const [absenceIdToDelete, setAbsenceIdToDelete] = useState<string | null>(null);

	const openCreate = useCallback((preset: CreatePreset = {}) => {
		setCreatePreset(preset);
		setCreateIsOpen(true);
	}, []);
	const openEdit = setAbsenceIdToUpdate;
	const openDelete = setAbsenceIdToDelete;

	const remainingVacationDays = useMemo(
		() => vacationLedgerReport ? new Decimal(parseFloat(vacationLedgerReport.daysRemaining)) : null,
		[vacationLedgerReport],
	);

	const absenceDataToUpdate = useMemo(
		() => absences.find((absence) => absence.id === absenceIdToUpdate) ?? null,
		[absenceIdToUpdate, absences],
	);

	const absenceDataToDelete = useMemo(() => {
		const absence = absences.find((absence) => absence.id === absenceIdToDelete);
		if (!absence) return null;
		const typeName = absenceTypes.find((absenceType) => absenceType.id === absence.absenceTypeId)?.name || "";
		return { ...absence, typeName };
	}, [absenceIdToDelete, absences, absenceTypes]);

	const createPanelInputs = remainingVacationDays && workingSchedules && holidays ? {
		remainingVacationDays,
		workingSchedules,
		staffMemberId,
		userCanManageLongTermAbsences: userCanManageAbsences,
	} : null;

	const mountsCreateSidebar = useLazyDialog(CreateAbsenceSidebar, createIsOpen);
	const mountsUpdateSidebar = useLazyDialog(UpdateAbsenceSidebar, !!absenceDataToUpdate);
	const mountsDeleteModal = useLazyDialog(DeleteAbsenceModal, !!absenceDataToDelete);

	const dialogs = createPanelInputs ? (
		<>
			{mountsCreateSidebar && <Suspense fallback={null}><CreateAbsenceSidebar
				isOpen={createIsOpen}
				onClose={() => setCreateIsOpen(false)}
				presetAbsenceTypeId={createPreset.absenceTypeId}
				presetDate={createPreset.date}
				{...createPanelInputs}
			/></Suspense>}
			{mountsUpdateSidebar && <Suspense fallback={null}><UpdateAbsenceSidebar
				isOpen={!!absenceDataToUpdate}
				absenceData={absenceDataToUpdate}
				onClose={() => setAbsenceIdToUpdate(null)}
				remainingVacationDays={createPanelInputs.remainingVacationDays}
				staffMemberId={staffMemberId}
				userCanManageLongTermAbsences={userCanManageAbsences}
				workingSchedules={createPanelInputs.workingSchedules} /></Suspense>}
			{mountsDeleteModal && <Suspense fallback={null}><DeleteAbsenceModal absenceData={absenceDataToDelete}
								isOpen={!!absenceDataToDelete}
								onCloseClick={() => setAbsenceIdToDelete(null)} /></Suspense>}
		</>
	) : null;

	return { createPanelInputs, dialogs, openCreate, openDelete, openEdit };
};
